import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "@finitless/shared/hooks";
import { handleApiError, handleApiSuccess } from "@finitless/shared/lib";
import { stripHtml } from "../../../../constants/campaignConstants";
import { Icon } from "../../../ui/UIElements/Icon";
import { Switch } from "../../../ui/UIElements/Switch";
import {
  useCreateCartRecoveryRule,
  useUpdateCartRecoveryRule,
  useCreateCartRecoveryChannel,
  useUpdateCartRecoveryChannel,
  useDeleteCartRecoveryChannel,
} from "../../../../hooks/useCartRecovery";
import CartRecoveryHtmlEditor from "./CartRecoveryHtmlEditor";

const SECONDS_PER_DAY = 86400;

const DEFAULT_EMAIL_BODY = "<p><br></p>";

const createClientKey = () =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `k-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

const plainTextToEmailHtml = (text) => {
  const safe = (text || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  const withBreaks = safe.split("\n").join("<br>");
  return `<p>${withBreaks || "<br>"}</p>`;
};

/**
 * @param {number} totalSeconds
 * @returns {{ timeMode: "time" | "days", days: number, h: number, m: number, s: number }}
 */
const secondsToFormTiming = (totalSeconds) => {
  const s = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  if (s > 0 && s % SECONDS_PER_DAY === 0) {
    return {
      timeMode: "days",
      days: s / SECONDS_PER_DAY,
      h: 0,
      m: 0,
      sec: 0,
    };
  }
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return {
    timeMode: "time",
    days: 1,
    h,
    m,
    sec,
  };
};

/**
 * @param {"time"|"days"} timeMode
 * @param {{ h: number, m: number, s: number, days: number }} parts
 * @returns {number[]}
 */
const buildTimesArray = (timeMode, parts) => {
  if (timeMode === "days") {
    const d = Math.max(0, parseInt(String(parts.days), 10) || 0);
    return [d * SECONDS_PER_DAY];
  }
  const hh = Math.max(0, parseInt(String(parts.h), 10) || 0);
  const mm = Math.min(59, Math.max(0, parseInt(String(parts.m), 10) || 0));
  const ss = Math.min(59, Math.max(0, parseInt(String(parts.s), 10) || 0));
  return [hh * 3600 + mm * 60 + ss];
};

const isBodyEmpty = (channel, body) => {
  const raw = (body || "").trim();
  if (!raw) return true;
  if (channel === "sms") return stripHtml(raw).trim().length === 0;
  return stripHtml(raw).trim().length === 0;
};

const normalizeNotificationChannel = (c) => {
  const v = (c || "").toLowerCase();
  return v === "sms" ? "sms" : "email";
};

const CartRecoveryForm = ({
  mode = "create",
  ruleId: ruleIdProp,
  initialRule = null,
  initialChannels = [],
  onCancel,
  onSuccess,
}) => {
  const { t } = useTranslation("cartRecovery");
  const { t: tCommon } = useTranslation("common");
  const toast = useToast();

  const createRule = useCreateCartRecoveryRule();
  const updateRule = useUpdateCartRecoveryRule();
  const createChannel = useCreateCartRecoveryChannel();
  const updateChannel = useUpdateCartRecoveryChannel();
  const deleteChannel = useDeleteCartRecoveryChannel();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [ruleEnabled, setRuleEnabled] = useState(true);
  const [timeMode, setTimeMode] = useState("time");
  const [h, setH] = useState(0);
  const [m, setM] = useState(0);
  const [s, setS] = useState(0);
  const [days, setDays] = useState(1);

  const [notifications, setNotifications] = useState(() => [
    {
      clientKey: createClientKey(),
      serverId: null,
      title: "",
      channel: "email",
      body: DEFAULT_EMAIL_BODY,
      enabled: true,
    },
  ]);

  const [initialChannelSnapshot, setInitialChannelSnapshot] = useState([]);

  const filteredInitialChannels = useMemo(
    () =>
      (initialChannels || []).filter((ch) =>
        ["email", "sms"].includes((ch.channel || "").toLowerCase()),
      ),
    [initialChannels],
  );

  const initialChannelsSignature = useMemo(
    () => JSON.stringify(filteredInitialChannels),
    [filteredInitialChannels],
  );

  const resetFromProps = useCallback(() => {
    if (mode !== "edit" || !initialRule) return;

    setName(initialRule.name || "");
    setDescription(initialRule.description || "");
    setRuleEnabled(initialRule.enabled !== false);

    const times = initialRule.times;
    const first = Array.isArray(times) && times.length > 0 ? times[0] : 0;
    const timing = secondsToFormTiming(first);
    setTimeMode(timing.timeMode);
    setH(timing.h);
    setM(timing.m);
    setS(timing.s);
    setDays(timing.days);

    if (filteredInitialChannels.length > 0) {
      const rows = filteredInitialChannels.map((ch) => ({
        clientKey: createClientKey(),
        serverId: ch.id ?? null,
        title: ch.title || "",
        channel: normalizeNotificationChannel(ch.channel),
        body:
          normalizeNotificationChannel(ch.channel) === "email"
            ? ch.body || DEFAULT_EMAIL_BODY
            : stripHtml(ch.body || ""),
        enabled: ch.enabled !== false,
      }));
      setNotifications(rows);
      setInitialChannelSnapshot(
        rows.map((r) => ({
          serverId: r.serverId,
          title: r.title,
          channel: r.channel,
          body: r.body,
          enabled: r.enabled,
        })),
      );
    } else {
      const emptyRow = {
        clientKey: createClientKey(),
        serverId: null,
        title: "",
        channel: "email",
        body: DEFAULT_EMAIL_BODY,
        enabled: true,
      };
      setNotifications([emptyRow]);
      setInitialChannelSnapshot([]);
    }
  }, [mode, initialRule, filteredInitialChannels]);

  useEffect(() => {
    if (mode !== "edit" || !initialRule?.id) return;
    resetFromProps();
  }, [mode, initialRule?.id, initialChannelsSignature, resetFromProps]);

  const handleAddNotification = () => {
    setNotifications((prev) => [
      ...prev,
      {
        clientKey: createClientKey(),
        serverId: null,
        title: "",
        channel: "email",
        body: DEFAULT_EMAIL_BODY,
        enabled: true,
      },
    ]);
  };

  const handleRemoveNotification = (clientKey) => {
    setNotifications((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((row) => row.clientKey !== clientKey);
    });
  };

  const handleNotificationField = (clientKey, field, value) => {
    setNotifications((prev) =>
      prev.map((row) => {
        if (row.clientKey !== clientKey) return row;
        if (field === "channel") {
          const next = normalizeNotificationChannel(value);
          if (next === row.channel) return row;
          if (next === "sms") {
            return {
              ...row,
              channel: "sms",
              body: stripHtml(row.body || ""),
            };
          }
          return {
            ...row,
            channel: "email",
            body: plainTextToEmailHtml(row.body || ""),
          };
        }
        return { ...row, [field]: value };
      }),
    );
  };

  const buildRulePayload = () => ({
    enabled: ruleEnabled,
    launch_type: "times",
    name: name.trim(),
    type: "abandoned_cart",
    description: description.trim(),
    times: buildTimesArray(timeMode, { h, m, s, days }),
  });

  const channelPayloadFromRow = (row) => {
    const ch = row.channel;
    const body =
      ch === "email"
        ? row.body || DEFAULT_EMAIL_BODY
        : stripHtml(row.body || "").trim();
    return {
      enabled: row.enabled !== false,
      channel: ch,
      body,
      title: row.title.trim(),
    };
  };

  const isRowDirty = (row) => {
    if (mode !== "edit" || !row.serverId) return true;
    const snap = initialChannelSnapshot.find(
      (s) => s.serverId === row.serverId,
    );
    if (!snap) return true;
    const next = channelPayloadFromRow(row);
    const prev = {
      enabled: snap.enabled !== false,
      channel: snap.channel,
      body:
        snap.channel === "email"
          ? snap.body || ""
          : stripHtml(snap.body || "").trim(),
      title: (snap.title || "").trim(),
    };
    return (
      next.title !== prev.title ||
      next.channel !== prev.channel ||
      next.body !== prev.body ||
      next.enabled !== prev.enabled
    );
  };

  const validate = () => {
    if (!name.trim()) {
      toast.error(t("form.validation.nameRequired"));
      return false;
    }
    if (timeMode === "days") {
      const d = parseInt(String(days), 10) || 0;
      if (d < 1) {
        toast.error(t("form.validation.daysMin"));
        return false;
      }
    }
    if (notifications.length === 0) {
      toast.error(t("form.validation.notificationsMin"));
      return false;
    }
    for (let i = 0; i < notifications.length; i += 1) {
      const row = notifications[i];
      if (!row.title.trim()) {
        toast.error(t("form.validation.notificationTitle", { index: i + 1 }));
        return false;
      }
      if (isBodyEmpty(row.channel, row.body)) {
        toast.error(t("form.validation.notificationBody", { index: i + 1 }));
        return false;
      }
    }
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;

    const rulePayload = buildRulePayload();

    try {
      if (mode === "create") {
        const created = await createRule.mutateAsync(rulePayload);
        const newId = created?.id ?? created?.result?.id;
        if (!newId) {
          throw new Error(t("form.errors.noRuleId"));
        }
        for (const row of notifications) {
          await createChannel.mutateAsync({
            ruleId: newId,
            data: channelPayloadFromRow(row),
          });
        }
        handleApiSuccess(t("form.toasts.createSuccess"), toast, {
          title: tCommon("states.success"),
        });
        onSuccess?.(created);
        return;
      }

      const rid = ruleIdProp ?? initialRule?.id;
      if (!rid) {
        throw new Error(t("form.errors.noRuleId"));
      }

      await updateRule.mutateAsync({ ruleId: rid, data: rulePayload });

      const initialIds = new Set(
        initialChannelSnapshot.map((s) => s.serverId).filter(Boolean),
      );
      const currentIds = new Set(
        notifications.map((n) => n.serverId).filter(Boolean),
      );
      for (const id of initialIds) {
        if (!currentIds.has(id)) {
          await deleteChannel.mutateAsync({ ruleId: rid, channelId: id });
        }
      }

      for (let i = 0; i < notifications.length; i += 1) {
        const row = notifications[i];
        const data = channelPayloadFromRow(row);
        if (row.serverId) {
          if (isRowDirty(row)) {
            await updateChannel.mutateAsync({
              ruleId: rid,
              channelId: row.serverId,
              data,
            });
          }
        } else {
          await createChannel.mutateAsync({ ruleId: rid, data });
        }
      }

      handleApiSuccess(t("form.toasts.updateSuccess"), toast, {
        title: tCommon("states.success"),
      });
      onSuccess?.();
    } catch (error) {
      handleApiError(error, toast, {
        title: tCommon("states.error"),
        defaultMessage: t("form.toasts.error"),
      });
    }
  };

  const isBusy =
    createRule.isPending ||
    updateRule.isPending ||
    createChannel.isPending ||
    updateChannel.isPending ||
    deleteChannel.isPending;

  return (
    <div className="space-y-8">
      <div className="rounded-xl border border-gray-800 bg-gray-900/80 p-6 shadow-lg shadow-black/20">
        <h2 className="mb-4 text-base font-semibold text-white">
          {t("form.general.title")}
        </h2>
        <div className="space-y-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <label
              htmlFor="cart-recovery-rule-enabled"
              className="text-sm font-medium text-gray-300"
            >
              {t("form.general.ruleEnabled")}
            </label>
            <Switch
              id="cart-recovery-rule-enabled"
              checked={ruleEnabled}
              onCheckedChange={setRuleEnabled}
              aria-label={t("form.general.ruleEnabled")}
            />
          </div>
          <div>
            <label
              htmlFor="cart-recovery-name"
              className="mb-1.5 block text-sm font-medium text-gray-400"
            >
              {t("form.general.name")}
            </label>
            <input
              id="cart-recovery-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="off"
              placeholder={t("form.general.namePlaceholder")}
              className="w-full rounded-lg border border-gray-700 bg-gray-950 px-4 py-2.5 text-white placeholder-gray-600 outline-none focus:border-transparent focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <div>
            <label
              htmlFor="cart-recovery-description"
              className="mb-1.5 block text-sm font-medium text-gray-400"
            >
              {t("form.general.description")}
            </label>
            <textarea
              id="cart-recovery-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder={t("form.general.descriptionPlaceholder")}
              className="w-full resize-y rounded-lg border border-gray-700 bg-gray-950 px-4 py-2.5 text-white placeholder-gray-600 outline-none focus:border-transparent focus:ring-2 focus:ring-purple-500"
            />
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-gray-800 bg-gray-900/80 p-6 shadow-lg shadow-black/20">
        <h2 className="mb-4 text-base font-semibold text-white">
          {t("form.timing.title")}
        </h2>
        <p className="mb-4 text-sm text-gray-500">
          {t("form.timing.subtitle")}
        </p>
        <fieldset className="space-y-4">
          <legend className="sr-only">{t("form.timing.title")}</legend>
          <div className="flex flex-col gap-3 sm:flex-row sm:gap-8">
            <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-300">
              <input
                type="radio"
                name="cart-recovery-time-mode"
                value="time"
                checked={timeMode === "time"}
                onChange={() => setTimeMode("time")}
                className="h-4 w-4 border-gray-600 text-emerald-500 focus:ring-purple-500"
              />
              {t("form.timing.time")}
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-300">
              <input
                type="radio"
                name="cart-recovery-time-mode"
                value="days"
                checked={timeMode === "days"}
                onChange={() => setTimeMode("days")}
                className="h-4 w-4 border-gray-600 text-emerald-500 focus:ring-purple-500"
              />
              {t("form.timing.days")}
            </label>
          </div>

          {timeMode === "time" ? (
            <div
              className="flex flex-wrap items-end gap-3"
              role="group"
              aria-label={t("form.timing.time")}
            >
              <div>
                <span className="mb-1 block text-xs text-gray-500">
                  {t("form.timing.hours")}
                </span>
                <input
                  type="number"
                  min={0}
                  value={h}
                  onChange={(e) =>
                    setH(Math.max(0, parseInt(e.target.value, 10) || 0))
                  }
                  className="w-20 rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 text-center text-white focus:ring-2 focus:ring-purple-500 focus:outline-none"
                  aria-label={t("form.timing.hours")}
                />
              </div>
              <span className="pb-2 text-gray-500" aria-hidden="true">
                :
              </span>
              <div>
                <span className="mb-1 block text-xs text-gray-500">
                  {t("form.timing.minutes")}
                </span>
                <input
                  type="number"
                  min={0}
                  max={59}
                  value={m}
                  onChange={(e) =>
                    setM(
                      Math.min(
                        59,
                        Math.max(0, parseInt(e.target.value, 10) || 0),
                      ),
                    )
                  }
                  className="w-20 rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 text-center text-white focus:ring-2 focus:ring-purple-500 focus:outline-none"
                  aria-label={t("form.timing.minutes")}
                />
              </div>
              <span className="pb-2 text-gray-500" aria-hidden="true">
                :
              </span>
              <div>
                <span className="mb-1 block text-xs text-gray-500">
                  {t("form.timing.seconds")}
                </span>
                <input
                  type="number"
                  min={0}
                  max={59}
                  value={s}
                  onChange={(e) =>
                    setS(
                      Math.min(
                        59,
                        Math.max(0, parseInt(e.target.value, 10) || 0),
                      ),
                    )
                  }
                  className="w-20 rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 text-center text-white focus:ring-2 focus:ring-purple-500 focus:outline-none"
                  aria-label={t("form.timing.seconds")}
                />
              </div>
            </div>
          ) : (
            <div>
              <label
                htmlFor="cart-recovery-days"
                className="mb-1.5 block text-sm text-gray-400"
              >
                {t("form.timing.daysLabel")}
              </label>
              <input
                id="cart-recovery-days"
                type="number"
                min={1}
                value={days}
                onChange={(e) =>
                  setDays(Math.max(1, parseInt(e.target.value, 10) || 1))
                }
                className="w-full max-w-xs rounded-lg border border-gray-700 bg-gray-950 px-4 py-2.5 text-white focus:ring-2 focus:ring-purple-500 focus:outline-none sm:w-48"
              />
            </div>
          )}
        </fieldset>
      </div>

      <div className="rounded-xl border border-gray-800 bg-gray-900/80 p-6 shadow-lg shadow-black/20">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-base font-semibold text-white">
            {t("form.notifications.title")}
          </h2>
          <button
            type="button"
            onClick={handleAddNotification}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-cyan-600/90 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-cyan-500 focus:ring-2 focus:ring-cyan-400 focus:outline-none"
          >
            <Icon name="Plus" className="h-4 w-4" aria-hidden="true" />
            {t("form.notifications.add")}
          </button>
        </div>

        <div className="space-y-6">
          {notifications.map((row, index) => (
            <div
              key={row.clientKey}
              className="rounded-lg border border-gray-800 bg-gray-950/50 p-4"
            >
              <div className="mb-3 flex items-center justify-between gap-2">
                <span className="text-xs font-medium tracking-wider text-gray-500 uppercase">
                  {t("form.notifications.itemLabel", { index: index + 1 })}
                </span>
                {notifications.length > 1 ? (
                  <button
                    type="button"
                    onClick={() => handleRemoveNotification(row.clientKey)}
                    className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-red-500/10 hover:text-red-400 focus:ring-2 focus:ring-red-500/50 focus:outline-none"
                    aria-label={t("form.notifications.remove")}
                  >
                    <Icon name="Trash2" className="h-4 w-4" />
                  </button>
                ) : null}
              </div>

              <div className="space-y-4">
                <div>
                  <label
                    className="mb-1.5 block text-sm font-medium text-gray-400"
                    htmlFor={`cart-recovery-ch-title-${row.clientKey}`}
                  >
                    {t("form.notifications.titleField")}
                  </label>
                  <input
                    id={`cart-recovery-ch-title-${row.clientKey}`}
                    type="text"
                    value={row.title}
                    onChange={(e) =>
                      handleNotificationField(
                        row.clientKey,
                        "title",
                        e.target.value,
                      )
                    }
                    placeholder={t("form.notifications.titlePlaceholder")}
                    className="w-full rounded-lg border border-gray-700 bg-gray-950 px-4 py-2.5 text-white placeholder-gray-600 outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                <div>
                  <label
                    className="mb-1.5 block text-sm font-medium text-gray-400"
                    htmlFor={`cart-recovery-ch-channel-${row.clientKey}`}
                  >
                    {t("form.notifications.channel")}
                  </label>
                  <select
                    id={`cart-recovery-ch-channel-${row.clientKey}`}
                    value={row.channel}
                    onChange={(e) =>
                      handleNotificationField(
                        row.clientKey,
                        "channel",
                        e.target.value,
                      )
                    }
                    className="custom-select w-full rounded-lg border border-gray-700 bg-gray-950 px-4 py-2.5 text-white focus:ring-2 focus:ring-purple-500 focus:outline-none"
                  >
                    <option value="email">{t("form.channel.email")}</option>
                    <option value="sms">{t("form.channel.sms")}</option>
                  </select>
                </div>

                <div>
                  <span className="mb-1.5 block text-sm font-medium text-gray-400">
                    {t("form.notifications.message")}
                  </span>
                  {row.channel === "email" ? (
                    <div className="cart-recovery-quill rounded-lg border border-gray-700 [&_.ql-container]:min-h-[220px] [&_.ql-container]:rounded-b-lg [&_.ql-container]:border-gray-700 [&_.ql-container]:bg-gray-950 [&_.ql-editor]:min-h-[200px] [&_.ql-editor]:text-sm [&_.ql-editor]:text-gray-100 [&_.ql-stroke]:stroke-gray-400 [&_.ql-toolbar]:rounded-t-lg [&_.ql-toolbar]:border-gray-700 [&_.ql-toolbar]:bg-gray-900">
                      <CartRecoveryHtmlEditor
                        value={row.body}
                        onChange={(html) =>
                          handleNotificationField(row.clientKey, "body", html)
                        }
                      />
                    </div>
                  ) : (
                    <textarea
                      value={row.body}
                      onChange={(e) =>
                        handleNotificationField(
                          row.clientKey,
                          "body",
                          e.target.value,
                        )
                      }
                      rows={6}
                      placeholder={t("form.notifications.smsPlaceholder")}
                      className="w-full resize-y rounded-lg border border-gray-700 bg-gray-950 px-4 py-3 font-mono text-sm text-gray-100 placeholder-gray-600 outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  )}
                </div>

                <div className="flex items-center justify-between gap-4">
                  <label
                    htmlFor={`cart-recovery-ch-enabled-${row.clientKey}`}
                    className="text-sm text-gray-400"
                  >
                    {t("form.notifications.channelEnabled")}
                  </label>
                  <Switch
                    id={`cart-recovery-ch-enabled-${row.clientKey}`}
                    checked={row.enabled}
                    onCheckedChange={(checked) =>
                      handleNotificationField(row.clientKey, "enabled", checked)
                    }
                    aria-label={t("form.notifications.channelEnabled")}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
        {onCancel ? (
          <button
            type="button"
            onClick={onCancel}
            disabled={isBusy}
            className="inline-flex items-center justify-center rounded-lg border border-gray-700 bg-gray-800 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-gray-700 disabled:opacity-50"
          >
            {tCommon("actions.cancel")}
          </button>
        ) : null}
        <button
          type="button"
          onClick={handleSave}
          disabled={isBusy}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-purple-600 px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-purple-500 focus:ring-2 focus:ring-purple-400 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isBusy ? (
            <Icon
              name="Loader"
              className="h-4 w-4 animate-spin"
              aria-hidden="true"
            />
          ) : (
            <Icon name="Check" className="h-4 w-4" aria-hidden="true" />
          )}
          {mode === "create"
            ? t("form.actions.submitCreate")
            : t("form.actions.submitSave")}
        </button>
      </div>
    </div>
  );
};

export default CartRecoveryForm;
